<template>
  <div class="kb-series-list-page">
    <header class="page-header">
      <div class="header-left">
        <el-button
          text
          @click="$router.back()"
        >
          返回
        </el-button>
        <div class="header-title-wrap">
          <h1 class="page-title">
            {{ categoryName || '专题列表' }}
          </h1>
          <p class="page-subtitle">
            {{ loading ? '加载中...' : `共 ${total} 个专题` }}
          </p>
        </div>
      </div>
      <div class="header-right">
        <el-input
          v-model="keyword"
          placeholder="搜索专题标题/摘要"
          clearable
          class="search-input"
          @keyup.enter="reloadFirstPage"
        />
        <el-button
          type="primary"
          @click="reloadFirstPage"
        >
          搜索
        </el-button>
      </div>
    </header>

    <!-- 骨架屏：与真实卡片同布局，刷新后立即渲染以提前 LCP -->
    <el-row
      v-if="loading"
      :gutter="16"
      class="grid grid-skeleton"
    >
      <el-col
        v-for="i in 8"
        :key="'skeleton-' + i"
        :xs="24"
        :sm="12"
        :md="8"
        :lg="6"
      >
        <div class="series-card series-card--skeleton">
          <div class="card-top">
            <div class="cover placeholder skeleton-cover" />
          </div>
          <div class="card-body">
            <div class="skeleton-meta">
              <div class="skeleton-pill" />
              <div class="skeleton-pill skeleton-pill--short" />
            </div>
            <div class="skeleton-line skeleton-title" />
            <div class="card-summary skeleton-summary">
              <span class="skeleton-line-inner" />
              <span class="skeleton-line-inner" />
            </div>
          </div>
        </div>
      </el-col>
    </el-row>

    <el-empty
      v-else-if="seriesList.length === 0"
      description="该分类下暂无专题"
    />

    <el-row
      v-else
      :gutter="16"
      class="grid"
    >
      <el-col
        v-for="s in seriesList"
        :key="s.id"
        :xs="24"
        :sm="12"
        :md="8"
        :lg="6"
      >
        <el-card
          class="series-card"
          shadow="hover"
        >
          <div
            class="card-top"
            @click="goToSeries(s.id)"
          >
            <el-image
              v-if="s.coverUrl"
              :src="getCoverThumbSrc(s)"
              fit="cover"
              class="cover"
            />
            <div
              v-else
              class="cover placeholder"
            >
              无封面
            </div>
            <el-button
              class="favorite-btn"
              :class="{ 'is-favorited': s.isFavorited }"
              circle
              text
              @click.stop="toggleFavorite(s)"
            >
              <el-icon>
                <StarFilled v-if="s.isFavorited" />
                <Star v-else />
              </el-icon>
            </el-button>
          </div>
          <div
            class="card-body"
            @click="goToSeries(s.id)"
          >
            <div class="card-meta">
              <el-tag
                size="small"
                :type="getLevelType(s.level)"
              >
                {{ getLevelText(s.level) }}
              </el-tag>
              <span
                v-if="s.favoriteCount != null"
                class="fav-count"
              >{{ s.favoriteCount }} 人收藏</span>
            </div>
            <div class="card-title">
              {{ s.title }}
            </div>
            <div class="card-summary">
              {{ s.summary || '暂无摘要' }}
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <div
      class="pager"
      v-if="total > 0"
    >
      <el-pagination
        background
        layout="prev, pager, next, sizes, total"
        :total="total"
        :current-page="current"
        :page-size="size"
        :page-sizes="[10, 20, 30, 50]"
        @update:current-page="handlePageChange"
        @update:page-size="handleSizeChange"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Star, StarFilled } from '@element-plus/icons-vue'
import request from '@/utils/request'
import { takePrefetchedKbSeriesList } from '@/utils/kbSeriesListPrefetch'
import {
  getSeriesListCache,
  setSeriesListCache,
  patchSeriesInCache,
  getCategoriesCache,
  setCategoriesCache
} from '@/utils/kbSeriesListCache'
import { getImageUrl } from '@/utils/imageUrl'
import { getThumbUrl } from '@/utils/imageThumbCache'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const loading = ref(true)
const categories = ref([])
const seriesList = ref([])
const total = ref(0)
const thumbSrcMap = ref({}) // seriesId -> thumb src

const current = ref(1)
const size = ref(20)
const keyword = ref('')

const categoryId = computed(() => route.query.categoryId)
const categoryName = computed(() => {
  const id = categoryId.value
  if (!id) return ''
  const cat = categories.value.find((c) => c.id === id)
  return cat?.name || ''
})

const getLevelType = (level) => {
  if (typeof level !== 'number') return ''
  if (level <= 10) return 'success'
  if (level <= 20) return 'warning'
  return 'danger'
}

const getLevelText = (level) => {
  if (typeof level === 'number') return `level:${level}`
  return level || '未知'
}

const getCoverThumbSrc = (series) => {
  if (!series?.coverUrl) return ''
  return thumbSrcMap.value[series.id] || getImageUrl(series.coverUrl)
}

const loadCategories = async () => {
  try {
    const data = await request.get('/kb/categories')
    categories.value = Array.isArray(data) ? data : []
    setCategoriesCache(categories.value)
  } catch (e) {
    categories.value = []
  }
}

/**
 * @param {{ silent?: boolean }} opts silent=true：后台刷新，不显示骨架屏
 */
const loadSeries = async (opts = {}) => {
  const silent = !!opts.silent
  if (!categoryId.value) {
    seriesList.value = []
    total.value = 0
    loading.value = false
    return
  }
  const fetchCid = categoryId.value
  const fetchCur = current.value
  const fetchSize = size.value
  const fetchKw = keyword.value?.trim() || ''
  if (!silent) loading.value = true
  try {
    const data = await request.get('/kb/series', {
      params: {
        current: fetchCur,
        size: fetchSize,
        categoryId: fetchCid,
        keyword: fetchKw
      }
    })
    if (
      categoryId.value !== fetchCid ||
      current.value !== fetchCur ||
      size.value !== fetchSize ||
      (keyword.value?.trim() || '') !== fetchKw
    ) {
      return
    }
    seriesList.value = data.records || []
    total.value = data.total || 0
    setSeriesListCache(fetchCid, fetchCur, fetchSize, fetchKw, seriesList.value, total.value)
  } catch (e) {
    if (!silent) {
      ElMessage.error('加载专题列表失败')
      seriesList.value = []
      total.value = 0
    }
  } finally {
    if (!silent) loading.value = false
  }
}

const reloadFirstPage = () => {
  current.value = 1
  loadSeries()
}

const handlePageChange = (p) => {
  current.value = p
  const cid = categoryId.value
  const kw = keyword.value?.trim() || ''
  const cached = cid ? getSeriesListCache(cid, p, size.value, kw) : null
  if (cached) {
    seriesList.value = cached.records
    total.value = cached.total
    loading.value = false
    loadSeries({ silent: true })
  } else {
    loadSeries()
  }
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const handleSizeChange = (s) => {
  size.value = s
  current.value = 1
  loadSeries()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const goToSeries = (id) => {
  router.push(`/kb/series/${id}`)
}

const toggleFavorite = async (series) => {
  if (!authStore.isAuthenticated()) {
    ElMessage.warning('请先登录')
    return
  }
  
  try {
    await request.post('/kb/favorites', {
      targetType: 'SERIES',
      targetId: series.id
    })
    // 更新本地状态
    series.isFavorited = !series.isFavorited
    patchSeriesInCache(
      categoryId.value,
      current.value,
      size.value,
      keyword.value?.trim() || '',
      series.id,
      { isFavorited: series.isFavorited }
    )
  } catch (error) {
    ElMessage.error('操作失败')
  }
}

onMounted(async () => {
  const cid = categoryId.value
  const catCached = getCategoriesCache()
  if (catCached?.length) categories.value = catCached
  else await loadCategories()

  const prefetched = cid ? await takePrefetchedKbSeriesList(cid) : null
  if (prefetched && (Array.isArray(prefetched.categories) || Array.isArray(prefetched.records))) {
    if (Array.isArray(prefetched.categories)) {
      categories.value = prefetched.categories
      setCategoriesCache(prefetched.categories)
    }
    seriesList.value = prefetched.records ?? []
    total.value = prefetched.total ?? 0
    loading.value = false
    setSeriesListCache(cid, current.value, size.value, keyword.value?.trim() || '', seriesList.value, total.value)
    await loadSeries({ silent: true })
  } else if (cid) {
    const kw = keyword.value?.trim() || ''
    const cached = getSeriesListCache(cid, current.value, size.value, kw)
    if (cached) {
      seriesList.value = cached.records
      total.value = cached.total
      loading.value = false
      await loadSeries({ silent: true })
    } else {
      if (!catCached?.length) await loadCategories()
      await loadSeries()
    }
  } else {
    loading.value = false
  }
})

// seriesList 列表变化时，异步生成/读取缩略图并缓存到本地
watch(seriesList, async (list) => {
  if (!Array.isArray(list) || list.length === 0) return
  const entries = await Promise.all(
    list
      .filter(s => s && s.id && s.coverUrl)
      .map(async (s) => {
        const full = getImageUrl(s.coverUrl)
        const thumb = await getThumbUrl(full, { width: 360, height: 240 })
        return [s.id, thumb]
      })
  )
  const next = { ...thumbSrcMap.value }
  for (const [id, src] of entries) next[id] = src
  thumbSrcMap.value = next
}, { immediate: true })

watch(
  () => route.query.categoryId,
  async () => {
    current.value = 1
    const cid = categoryId.value
    const kw = keyword.value?.trim() || ''
    if (!cid) {
      seriesList.value = []
      total.value = 0
      loading.value = false
      return
    }
    const cached = getSeriesListCache(cid, 1, size.value, kw)
    if (cached) {
      seriesList.value = cached.records
      total.value = cached.total
      loading.value = false
      await loadSeries({ silent: true })
    } else {
      await loadSeries()
    }
  }
)
</script>

<style scoped lang="scss">
.kb-series-list-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg) var(--spacing-xl);
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-sm);
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.header-title-wrap {
  .page-title {
    margin: 0 0 var(--spacing-xs) 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    line-height: var(--line-height-tight);
  }
  .page-subtitle {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }
}

.header-right {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
}

.search-input {
  width: 260px;
}

.grid {
  margin-top: var(--spacing-md);
  row-gap: var(--spacing-lg);
}

.series-card {
  cursor: pointer;
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: transform var(--transition-fast) var(--ease-out), box-shadow var(--transition-fast) var(--ease-out);
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  /* 去掉默认内边距，让封面贴齐卡片上沿（与圆角由 overflow + 封面自身圆角配合） */
  :deep(.el-card__body) {
    padding: 0;
  }
}

.card-top {
  position: relative;
}

.cover {
  width: 100%;
  height: 176px;
  display: block;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  overflow: hidden;

  :deep(.el-image__inner) {
    width: 100%;
    height: 100%;
  }
}

.cover.placeholder {
  background: var(--color-bg-hover);
  border: 1px dashed var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
}

.card-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: var(--spacing-sm);
}

.card-meta .fav-count {
  font-size: 12px;
  color: var(--color-text-tertiary, #909399);
}

.favorite-btn {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  color: #909399;
  font-size: 20px;
  transition: color 0.3s;
  
  &:hover {
    color: #f56c6c;
  }
  
  &.is-favorited {
    color: #f56c6c;
  }
}

.card-body {
  /* 封面与标题/摘要之间留白；左右与卡片边缘对齐 */
  padding: var(--spacing-lg) var(--spacing-md) var(--spacing-md);
}

.card-title {
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-summary {
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
  height: 36px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 骨架屏：与真实卡片同尺寸，首屏立即渲染以改善 LCP */
.series-card--skeleton {
  pointer-events: none;
}
.skeleton-cover {
  background: var(--color-bg-hover) !important;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}
.skeleton-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: var(--spacing-sm);
}
.skeleton-pill {
  height: 22px;
  width: 72px;
  border-radius: 4px;
  background: var(--color-bg-hover);
  animation: skeleton-pulse 1.5s ease-in-out infinite 0.05s;
}
.skeleton-pill--short {
  width: 56px;
  animation-delay: 0.08s;
}
.skeleton-title {
  height: 18px;
  border-radius: 4px;
  background: var(--color-bg-hover);
  animation: skeleton-pulse 1.5s ease-in-out infinite 0.1s;
}
.skeleton-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
  -webkit-line-clamp: unset;
  -webkit-box-orient: unset;
}
.skeleton-summary .skeleton-line-inner {
  display: block;
  height: 12px;
  border-radius: 4px;
  background: var(--color-bg-hover);
  animation: skeleton-pulse 1.5s ease-in-out infinite 0.2s;
}
.skeleton-summary .skeleton-line-inner:last-child {
  width: 75%;
}
@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.pager {
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-lg);
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    padding: var(--spacing-md);
  }
  .header-right .search-input {
    width: 100%;
  }
}
</style>

